import { Component, EventEmitter, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import {
  PgTitleComponent,
  PgToolbarComponent,
} from '../../../shared/components/title-bar/title-bar.component';
import _ from 'lodash';
import { OilToolbarComponent } from './oil-toolbar/oil-toolbar.component';

import {
  OrganisationService,
} from 'src/app/organisation/organisation.service';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import saveAs from 'file-saver';
import {
  MessageLevel,
  PgMessagesService,
} from 'src/app/shared/services/pg-messages.service';
import { DocumentService } from 'src/app/documents/documents.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { LuxonDateTimeHelpers } from 'src/app/shared/helpers/luxon-date-time.helpers';
import { OilDeliveryNote } from 'src/app/generated/services/transportation/reports/oil';
import { OilServiceSpa } from 'src/app/generated/services/transportation/reports/oilSpa';

@Component({
  selector: 'app-oils',
  templateUrl: 'oils.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule]
})
export class OilsComponent
implements OnInit, PgTitleComponent, PgToolbarComponent {
  title: EventEmitter<string> = new EventEmitter();
  oilDeliveryNotes!: OilDeliveryNote[];

  biggestSupplierProvidedTonnage!: number;
  biggestSupplierOrganisationId!: string;
  organisations!: Map<string, Organisation>;
  numberOfSuppliers!: number;
  numberOfReceivingLocations!: number;
  numberOfOils!: number;

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  constructor(
    private _organisationservice: OrganisationService,
    private _router: Router,
    private _documentsService: DocumentService,
    private _pgMessagesService: PgMessagesService,
    private _renderer: Renderer2,
    private _userLocalStorage: UserLocalStoreService,
    private _oilService: OilServiceSpa
  ) { }

  columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params: { data: { fileId: string; vesselName: any; bdnNumber: any; }; }) => {
        const container = this._renderer.createElement('div');
        if (params.data.fileId) {
          const downloadBtn = AgGridService.downloadButton(
            this._renderer,
            () => this.downloadFile(params.data.fileId, `${params.data.vesselName}-${params.data.bdnNumber}`),
          );
          this._renderer.appendChild(container, downloadBtn);
        }
        const editBtn = AgGridService.editButton(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data),
          params.data
        );
        this._renderer.appendChild(container, editBtn);
        return container;
      }
    },

    { headerName: 'BDN NO', field: 'bdnNumber' },
    { headerName: 'Vessel', field: 'vesselName' },
    {
      headerName: 'Supplier', field: 'supplierOrganisationId',
      cellRenderer: (row: any) => this.formatOrganisation(row.value)
    },
    {
      headerName: 'Location', field: 'locationOrganisationId',
      cellRenderer: (row: any) => this.formatOrganisation(row.value)
    },
    { headerName: 'Product', field: 'product' },
    { headerName: 'Quantity', field: 'quantity' },
    {
      headerName: 'Date', field: 'date',
      cellRenderer: (row: any) => LuxonDateTimeHelpers.offsetDateTimeToLocalString(row.data.date, 'yyyy-LL-dd')
    },
  ];

  ngOnInit(): void {
    this.gridOptions = AgGridService.getOptions();
    this.title.emit('Oil deliveries');
    forkJoin([
      this._oilService.getAllOilDeliveryNotes({}),
      this._organisationservice.getOrganisationIdToOrganisationMap(),
    ]).subscribe((value) => {
      this.oilDeliveryNotes = value[0].oils.sort((a, b) => new Date(b.date?.isoDate || '').getTime() - new Date(a.date?.isoDate || '').getTime());
      this.organisations = value[1];
      this.numberOfOils = this.oilDeliveryNotes.length;
      this.numberOfReceivingLocations = [
        ...new Set(this.oilDeliveryNotes.map((b) => b.locationOrganisationId)),
      ].length;
      this.numberOfSuppliers = [
        ...new Set(this.oilDeliveryNotes.map((b) => b.supplierOrganisationId)),
      ].length;
      const highest = _.chain(this.oilDeliveryNotes)
        .groupBy((b) => b.supplierOrganisationId)
        .map((v, k) => ({
          supplierOrganisationId: k,
          tons: v.reduce((a, b) => a + b.quantity, 0),
        }))
        .sort((a, b) => b.tons - a.tons)
        .value()[0];
      if (highest) {
        this.biggestSupplierOrganisationId = highest.supplierOrganisationId;
        this.biggestSupplierProvidedTonnage = highest.tons;
      }
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'oliGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'oliGrid');
  }

  toolbarComponentType = OilToolbarComponent;
  toolbarComponent: any;
  editRecord(odn: OilDeliveryNote) {
    this._router.navigate([
      'vessels/oil',
      odn.vesselImo,
      odn.bdnNumber,
      odn.product,
      odn.date?.isoDate,
    ]);
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe(
      (b) => {
        saveAs(b.body, `${prefix}.pdf`);
      },
      () => {
        this._pgMessagesService.publishMessage({
          message: 'Could not download this file.',
          level: MessageLevel.Error,
          topic: 'Oil document',
        });
      }
    );
  }

  formatOrganisation(organisationId: string) {
    const foundOrganisation = this.organisations?.get(organisationId);
    return foundOrganisation ? foundOrganisation.nameTranslations['en'] : '';
  }
}
